import { MoneyV2 } from "@ts/shopify-storefront";
import { useCallback } from "react";
import { useSale } from "./useSale";

import { useSite } from "~/hooks/useSite";
import { Badge } from "@ts/components";
import { ProductBadge } from "types/product";

export const useShopify = () => {
  const site = useSite();

  const { sale, isSaleActive } = useSale();
  const isOnSale = isSaleActive();

  const imageUrl = (src: string, size = -1): string => {
    const dimensions = `${size}x${size}`;
    const match = src?.match(
      /\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif)(\?v=\d+)?$/i,
    );

    return match &&
      src?.includes("shopify.com") &&
      !src?.includes("res.cloudinary.com/baredfootwear/image/fetch/f_auto") &&
      size !== -1
      ? `https://res.cloudinary.com/baredfootwear/image/fetch/f_auto/${src?.split(match[0])[0]}_${dimensions}${match[0]}`.replace(
          /http(s)?:/,
          ``,
        )
      : src;
  };

  const imageSrcSets = (src, size) =>
    src?.includes(`shopify.com`)
      ? [1, 150, 200, 400, 500, 768, 1000, 1500, 2000]
          .filter((set) => !size || (size && size >= set))
          .map((set) => `${imageUrl(src, set.toString())} ${set}w`)
          .join(`,`)
      : "";

  const decodeVariantId = (id?: string) => {
    return id ? id.split(`ProductVariant/`)[1] : id;
  };

  const getBadgeFromTags = useCallback(
    (
      tags?: string[],
      productStatus?: string,
      availableForSale?: boolean,
      launchDateMetafield?: string,
      prices?: {
        price: MoneyV2;
        compareAtPrice: MoneyV2 | null;
      },
    ): ProductBadge | null => {
      //Resolve notify me/out of stock badge based off inventory count and product status (Convert this to use availableForSale when possible)
      if (!availableForSale && productStatus) {
        return ["Seasonal", "Core"].includes(productStatus)
          ? { children: "Notify Me", variant: "secondary" }
          : { children: "Out Of Stock", variant: "warning" };
      }

      // If product has a discount, show the percentage off
      const compareAmount = Number(prices?.compareAtPrice?.amount ?? 0);
      const currentAmount = Number(prices?.price?.amount ?? 0);

      if (
        compareAmount > 0 &&
        currentAmount > 0 &&
        compareAmount > currentAmount
      ) {
        const exactDiscountPercentage =
          ((compareAmount - currentAmount) / compareAmount) * 100;
        // Add sensitivity buffer - anything within 0.05% of next tier gets bumped up
        const sensitivityBuffer = 0.05;
        const adjustedDiscountPercentage =
          exactDiscountPercentage + sensitivityBuffer;

        const roundedDiscount = Math.floor(adjustedDiscountPercentage / 5) * 5; // Round down to nearest 5
        return { children: `${roundedDiscount}% Off`, variant: "secondary" };
      }

      // If product is on preorder, set Preorder.
      if (tags?.includes("Preorder")) {
        return { children: "Pre-Order Now", variant: "light" };
      }

      // Check if we're in a country with retail stores (NZ) and product is tagged as OnlineOnly for that country
      if (
        ["NZ"].includes(site.countryCode) &&
        tags?.includes(`${site.countryCode}:OnlineOnly`)
      ) {
        return { children: "Online Only", variant: "light" };
      }

      // If the product is within last 4 weeks of launch date.
      if (launchDateMetafield) {
        const launchDate = new Date(launchDateMetafield);
        const now = new Date();
        const fourWeeksAgo = new Date();
        fourWeeksAgo.setDate(now.getDate() - 28);
        if (launchDate >= fourWeeksAgo) {
          return { children: "New", variant: "light" };
        }
      }

      // Keep track of the badge.
      let badge: ProductBadge | null = null;

      for (const tag of tags ?? []) {
        // Tag does not start with "badge", throw it away.
        if (!tag.startsWith("badge:")) continue;

        // Split the tag into it's parts.
        const tagParts = tag.split(":");
        let [_prefix, colour, label, countryCode, saleType] = tagParts;

        // No colour or label defined, bail out.
        if (!colour || !label) continue;

        // If the "country code" part looks like a sale type, then it has been omitted.
        if (
          !!countryCode &&
          ["vip", "sale"].includes(countryCode.toLowerCase())
        ) {
          saleType = countryCode;
          // @ts-expect-error
          countryCode = undefined;
        }

        // If this badge has a country code, but we are not in that country, bail out.
        if (
          !!countryCode &&
          site.countryCode?.localeCompare(countryCode) !== 0
        ) {
          continue;
        }

        // If badge has a sale type but we are not on sale, bail out.
        if (
          !!saleType &&
          ["vip", "sale"].includes(saleType.toLowerCase()) &&
          !isOnSale
        ) {
          continue;
        }

        // Set badge.
        badge = {
          children: label,
          variant: "light" === colour ? "light" : "secondary",
        };

        // Prevent further iterations.
        break;
      }

      // Return badge if found.
      return badge;
    },
    [site, sale, isOnSale],
  );

  return {
    decodeVariantId,
    getBadgeFromTags,
    imageSrcSets,
    imageUrl,
  };
};
